import React, { useEffect, useState } from 'react';
import { apiUrl } from '../../utils/apiBase';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Database,
  Save,
} from 'lucide-react';

interface Props {
  session: any;
}

// Toggles disponíveis na integração GIX. As chaves devem bater com
// INTEGRACOES_CONFIG_DEFAULT['gix'] no backend (server.py).
interface GixConfig {
  enabled: boolean;
  criarOc: boolean;
  liberarOc: boolean;
  estornarOc: boolean;
  badgeStatus: boolean;
  allowInsert: boolean;
}

const DEFAULTS: GixConfig = {
  enabled: false,
  criarOc: true,
  liberarOc: true,
  estornarOc: true,
  badgeStatus: true,
  allowInsert: false,
};

interface GixHealth {
  status: 'ok' | 'error';
  database: 'connected' | 'disconnected';
  allow_insert: boolean;
  empresas_permitidas: number[];
  db_host: string;
  error?: string;
}

const Toggle: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, checked, disabled, onChange }) => (
  <div className={`flex items-start justify-between gap-4 py-3 ${disabled ? 'opacity-50' : ''}`}>
    <div className="flex-1">
      <div className="text-sm font-medium text-gray-800">{label}</div>
      {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 ${
        checked ? 'bg-indigo-600' : 'bg-gray-300'
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  </div>
);

const IntegracaoGix: React.FC<Props> = ({ session }) => {
  const empresaId: string | undefined =
    session?.empresa?.id || session?.usuario?.empresaId;
  const usuarioNome: string | undefined =
    session?.usuario?.nome || session?.usuario?.email;

  const [config, setConfig] = useState<GixConfig>(DEFAULTS);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [health, setHealth] = useState<GixHealth | null>(null);
  const [healthCarregando, setHealthCarregando] = useState(true);

  const isLocal =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  // Carrega config do MongoDB
  useEffect(() => {
    if (!empresaId) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    fetch(apiUrl(`/integracoes/config?empresaId=${encodeURIComponent(empresaId)}`))
      .then(r => r.json())
      .then(j => {
        if (j?.status === 'success' && j?.config?.gix) {
          setConfig({ ...DEFAULTS, ...j.config.gix });
        }
      })
      .catch(e => setErro(`Falha ao carregar configuração: ${e?.message || e}`))
      .finally(() => setCarregando(false));
  }, [empresaId]);

  // Carrega health da api_gix (funciona local e VPS via DDNS)
  useEffect(() => {
    setHealthCarregando(true);
    fetch('/api/gix/health')
      .then(r => r.json().then(j => ({ ...j, _http: r.status })))
      .then(j => setHealth(j))
      .catch(() => setHealth({ status: 'error', database: 'disconnected', allow_insert: false, empresas_permitidas: [], db_host: '?', error: 'API GIX offline' }))
      .finally(() => setHealthCarregando(false));
  }, []);

  const salvar = async () => {
    if (!empresaId) {
      setErro('empresaId não encontrado na sessão. Faça login novamente.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const r = await fetch(apiUrl('/integracoes/config'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ empresaId, gix: config, usuario: usuarioNome }),
      });
      const j = await r.json();
      if (!r.ok || j?.status !== 'success') {
        throw new Error(j?.message || `HTTP ${r.status}`);
      }
      setSalvoEm(new Date());
    } catch (e: any) {
      setErro(`Falha ao salvar: ${e?.message || e}`);
    } finally {
      setSalvando(false);
    }
  };

  const set = <K extends keyof GixConfig>(k: K, v: GixConfig[K]) =>
    setConfig(c => ({ ...c, [k]: v }));

  if (carregando) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando configuração...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cabeçalho com status */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="bg-emerald-600 text-white rounded-lg w-10 h-10 flex items-center justify-center flex-shrink-0">
            <Database className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-emerald-900 flex items-center gap-2">
              GIX (ERP Refricril)
              {config.enabled ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wide">Ativa</span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-400 text-white uppercase tracking-wide">Desativada</span>
              )}
            </h3>
            <p className="text-xs text-emerald-800/80 mt-0.5">
              Cria, libera, estorna e consulta Ordens de Compra direto no Pyxis (PostgreSQL).
              Cada etapa do fluxo escolhe em qual campo aparecem os botões/badge.
            </p>
          </div>
        </div>
      </div>

      {/* Aviso ambiente */}
      {!isLocal && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Você está acessando pela <strong>VPS</strong>. A integração GIX está disponível via
            <strong> DDNS</strong> (a API GIX se conecta ao Pyxis pela rede externa). Os botões e
            badge funcionam normalmente nos cards.
          </span>
        </div>
      )}

      {/* Status da API GIX */}
      {(
        <div className="border border-gray-200 rounded-xl p-4 bg-white">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Status da API {isLocal ? 'local' : '(VPS via DDNS)'}</h4>
          {healthCarregando ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Verificando...
            </div>
          ) : health ? (
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2">
                {health.status === 'ok' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-gray-700">
                  api_gix.py em <code className="text-xs bg-gray-100 px-1 rounded">localhost:5002</code>{' '}
                  {health.status === 'ok' ? 'rodando' : 'offline'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {health.database === 'connected' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <span className="text-gray-700">
                  PostgreSQL <code className="text-xs bg-gray-100 px-1 rounded">{health.db_host}:5432</code>{' '}
                  {health.database === 'connected' ? 'conectado' : 'desconectado'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {health.allow_insert ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                )}
                <span className="text-gray-700">
                  <code className="text-xs bg-gray-100 px-1 rounded">GIX_OC_ALLOW_INSERT</code>{' '}
                  {health.allow_insert ? (
                    <span className="text-emerald-700 font-medium">ativado (INSERT real)</span>
                  ) : (
                    <span className="text-amber-700">desativado (DRY-RUN)</span>
                  )}
                </span>
              </div>
              {health.error && (
                <div className="text-xs text-red-700 mt-2 px-2 py-1 bg-red-50 rounded border border-red-200 font-mono">
                  {health.error}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Sem dados</div>
          )}
        </div>
      )}

      {/* Toggles */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white">
        <Toggle
          label="Integração GIX habilitada"
          description="Quando desligado, nenhum botão ou badge GIX aparece nos cards. Os toggles abaixo só funcionam quando este está ligado."
          checked={config.enabled}
          onChange={v => set('enabled', v)}
        />
        <hr className="my-1 border-gray-100" />
        <Toggle
          label="Criar OC"
          description="Mostra o botão verde 'Criar OC no GIX' nos cards (no campo configurado pela etapa)."
          checked={config.criarOc}
          disabled={!config.enabled}
          onChange={v => set('criarOc', v)}
        />
        <Toggle
          label="Liberar OC"
          description="Permite liberar (aprovar) uma OC já criada, direto pelo modal de detalhes."
          checked={config.liberarOc}
          disabled={!config.enabled}
          onChange={v => set('liberarOc', v)}
        />
        <Toggle
          label="Estornar OC"
          description="Permite estornar uma OC liberada, direto pelo modal de detalhes."
          checked={config.estornarOc}
          disabled={!config.enabled}
          onChange={v => set('estornarOc', v)}
        />
        <Toggle
          label="Badge de status da OC"
          description="Mostra o badge colorido com o status (Aberta / Liberada / Estornada / etc.) abaixo do campo configurado pela etapa."
          checked={config.badgeStatus}
          disabled={!config.enabled}
          onChange={v => set('badgeStatus', v)}
        />
        <hr className="my-1 border-gray-100" />
        <Toggle
          label="Permitir INSERT real (sair do DRY-RUN)"
          description={
            health && config.allowInsert !== health.allow_insert
              ? 'Atenção: este valor não bate com a variável de ambiente da API. Reinicie a api_gix.py com a env GIX_OC_ALLOW_INSERT=1 para que o INSERT realmente aconteça.'
              : 'Esta opção apenas indica a intenção. Para que o INSERT real ocorra, a api_gix.py também precisa estar com a variável de ambiente GIX_OC_ALLOW_INSERT=1.'
          }
          checked={config.allowInsert}
          disabled={!config.enabled}
          onChange={v => set('allowInsert', v)}
        />
      </div>

      {/* Botão salvar */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {salvoEm && <span className="text-emerald-700">✓ Salvo às {salvoEm.toLocaleTimeString()}</span>}
          {erro && <span className="text-red-600">{erro}</span>}
        </div>
        <button
          type="button"
          onClick={salvar}
          disabled={salvando || !empresaId}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg shadow-sm"
        >
          {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {salvando ? 'Salvando...' : 'Salvar configuração'}
        </button>
      </div>
    </div>
  );
};

export default IntegracaoGix;
